import React from 'react'
import useCart from '@/public/hooks/use-cart'
import { numberToCurrency } from '@/shared/helpers/String'
import CartLedgerItem from '@/public/cart/CartLedgerItem'

export default function CartLedger({ userSponsoredAllowance }) {
  // TAXES ARE NOT CALCULATED AT THIS TIME
  const taxesEnabled = false

  const { cart } = useCart()

  if (!cart) { return null }

  const totalAmount = cart.calculated_total_after_savings
    - cart.sponsored_allowance
    - (cart.supplement_invoicing_amount || 0)

  const subtotal = () => (
    <CartLedgerItem
      label="Subtotal"
      value={numberToCurrency(cart.calculated_total_before_savings)}
    />
  )

  const contractPricingSavings = () => {
    if (parseFloat(cart.contract_pricing_savings || 0) <= 0) { return null }

    return (
      <CartLedgerItem
        label="Employer Discount"
        value={`-${numberToCurrency(cart.contract_pricing_savings)}`}
      />
    )
  }

  const membershipSavings = () => {
    if (parseFloat(cart.membership_supplement_savings || 0) <= 0) { return null }

    return (
      <CartLedgerItem
        label="Member Savings"
        value={`-${numberToCurrency(cart.membership_supplement_savings)}`}
      />
    )
  }

  const savings = () => {
    // Show savings excluding contract pricing and membership discount
    const otherSavings = parseFloat(cart.calculated_amount_saved)
      - parseFloat(cart.contract_pricing_savings || 0)
      - parseFloat(cart.membership_supplement_savings || 0)
    if (otherSavings <= 0) { return null }

    return (
      <CartLedgerItem
        label="Savings"
        value={`-${numberToCurrency(otherSavings)}`}
      />
    )
  }

  const serviceFee = () => {
    // When the integration is on, the cart carries a per-company fee breakdown;
    // render one line per company. Otherwise keep the single lumped fee line.
    const fees = cart.calculated_service_fees

    if (fees && fees.length > 0) {
      return fees
        .filter((fee) => parseFloat(fee.amount) > 0)
        .map((fee) => (
          <CartLedgerItem
            key={fee.slug}
            label={`${fee.name} service fee`}
            value={numberToCurrency(fee.amount)}
          />
        ))
    }

    if (parseFloat(cart.calculated_service_fee) <= 0) { return null }

    return (
      <CartLedgerItem
        label="Lab Draw"
        value={numberToCurrency(cart.calculated_service_fee)}
      />
    )
  }

  const shipping = () => (
    <CartLedgerItem
      label="Shipping"
      value={numberToCurrency(cart.calculated_shipping_cost)}
    />
  )

  const supplementInvoicing = () => {
    const amount = parseFloat(cart.supplement_invoicing_amount || 0)
    if (amount <= 0) { return null }

    return (
      <CartLedgerItem
        label="Invoiced Supplements"
        value={`-${numberToCurrency(amount)}`}
      />
    )
  }

  const sponsoredAllowance = () => {
    if (!parseFloat(cart.sponsored_allowance)
      || parseFloat(cart.calculated_total_after_savings) <= 0
      || !parseFloat(userSponsoredAllowance)) {
      return null
    }
    const allowanceLeft = userSponsoredAllowance - cart.sponsored_allowance

    return (
      <>
        <CartLedgerItem
          label={`${numberToCurrency(userSponsoredAllowance)} Allowance`}
          value={`-${numberToCurrency(cart.sponsored_allowance)}`}
        />
        <div className="sm">
          <span className={`${allowanceLeft > 0 ? 'text-success' : 'text-danger'}`}>{numberToCurrency(allowanceLeft)}</span>
          {' '}
          <span>remaining</span>
        </div>
      </>
    )
  }

  const taxes = () => {
    if (!taxesEnabled) { return null }

    return (
      <CartLedgerItem
        label="Taxes"
        value=""
      />
    )
  }

  const total = () => (
    <CartLedgerItem
      label="Total"
      discountCode={cart.discount_code?.code}
      value={numberToCurrency(totalAmount)}
      className="fw-bold mt-2"
    />
  )

  return (
    <>
      {subtotal()}
      {contractPricingSavings()}
      {membershipSavings()}
      {savings()}
      {serviceFee()}
      {shipping()}
      {supplementInvoicing()}
      {sponsoredAllowance()}
      {taxes()}
      {total()}
    </>
  )
}
